import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useToast,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Center,
} from '@chakra-ui/react';
import {
  AddIcon,
  SearchIcon,
  ViewIcon,
  EditIcon,
  RepeatIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUrl';

interface Contract {
  id: string;
  customerName: string;
  customerEmail: string;
  companyName: string;
  planTier: 'starter' | 'professional' | 'enterprise';
  doorCount: number;
  monthlyValue: number;
  annualValue: number;
  paymentFrequency: 'monthly' | 'annual';
  startDate: string;
  endDate: string;
  status: 'active' | 'expiring_soon' | 'expired' | 'cancelled';
  autoRenew: boolean;
  renewalStatus?: 'pending' | 'renewed' | 'cancelled';
  daysRemaining?: number;
  createdAt: string;
}

interface ContractStats {
  activeContracts: number;
  totalContractValue: number;
  expiringSoon: number;
  renewalsThisQuarter: number;
}

const ContractsPage: React.FC = () => {
  const { t } = useTranslation('contracts');
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [contracts, setContracts] = useState<Contract[]>([]);
  const [stats, setStats] = useState<ContractStats>({
    activeContracts: 0,
    totalContractValue: 0,
    expiringSoon: 0,
    renewalsThisQuarter: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');

  useEffect(() => {
    fetchContracts();
    fetchStats();
  }, []);

  const fetchContracts = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getContracts?status=${statusFilter}&tier=${tierFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch contracts: ${response.status}`);
      }

      const data = await response.json();
      // Map contracts and calculate missing values
      const mappedContracts = (data.contracts || []).map((contract: Contract) => ({
        ...contract,
        // Calculate monthly and annual values if missing
        monthlyValue: contract.monthlyValue || (contract.annualValue ? contract.annualValue / 12 : 0),
        annualValue: contract.annualValue || (contract.monthlyValue ? contract.monthlyValue * 12 : 0),
        // Calculate plan tier from door count if missing
        planTier: contract.planTier || getPlanTier(contract.doorCount)
      }));
      setContracts(mappedContracts);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast({
        title: t('errors.fetchFailed'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!user) {
      return;
    }
    
    try {
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/getContractStats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data.stats || stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleRenewContract = async (contractId: string) => {
    navigate(`/admin/contracts/${contractId}/renew`);
  };

  const handleCancelContract = async (contractId: string) => {
    if (!confirm(t('confirmCancel'))) return;
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const apiUrl = getApiUrl();
      const response = await fetch(
        `${apiUrl}/cancelContract`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ contractId }),
        }
      );

      if (!response.ok) throw new Error('Failed to cancel contract');

      toast({
        title: t('success.cancelled'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchContracts();
      fetchStats();
    } catch (error) {
      toast({
        title: t('errors.cancelFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'expiring_soon':
        return 'orange';
      case 'expired':
        return 'red';
      case 'cancelled':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const getPlanTier = (doorCount: number): 'starter' | 'professional' | 'enterprise' => {
    if (doorCount <= 10) return 'starter';
    if (doorCount <= 50) return 'professional';
    return 'enterprise';
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'starter':
        return 'blue';
      case 'professional':
        return 'purple';
      case 'enterprise':
        return 'pink';
      default:
        return 'gray';
    }
  };

  const getDaysRemainingColor = (days?: number) => {
    if (!days) return 'gray';
    if (days < 7) return 'red';
    if (days < 30) return 'orange';
    return 'green';
  };

  const filteredContracts = contracts.filter((contract) => {
    const matchesSearch =
      searchTerm === '' ||
      contract.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.companyName?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' || contract.status === statusFilter;

    const matchesTier = tierFilter === 'all' || contract.planTier === tierFilter;

    return matchesSearch && matchesStatus && matchesTier;
  });

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">{t('title')}</Heading>
        <Button
          leftIcon={<AddIcon />}
          colorScheme="brand"
          onClick={() => navigate('/admin/contracts/new')}
        >
          {t('createNew')}
        </Button>
      </Flex>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.active')}</StatLabel>
              <StatNumber>{stats.activeContracts}</StatNumber>
              <StatHelpText>{t('stats.activeHelp')}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.totalValue')}</StatLabel>
              <StatNumber>
                ${(stats.totalContractValue || 0).toLocaleString()}
              </StatNumber>
              <StatHelpText>{t('stats.totalValueHelp')}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.expiring')}</StatLabel>
              <StatNumber color="orange.500">{stats.expiringSoon}</StatNumber>
              <StatHelpText>{t('stats.expiringHelp')}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <Stat>
              <StatLabel>{t('stats.renewals')}</StatLabel>
              <StatNumber>{stats.renewalsThisQuarter}</StatNumber>
              <StatHelpText>{t('stats.renewalsHelp')}</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Card mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">{t('filters.allStatuses')}</option>
              <option value="active">{t('filters.active')}</option>
              <option value="expiring_soon">
                {t('filters.expiring')}
              </option>
              <option value="expired">{t('filters.expired')}</option>
              <option value="cancelled">
                {t('filters.cancelled')}
              </option>
            </Select>

            <Select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
            >
              <option value="all">{t('filters.allTiers')}</option>
              <option value="starter">{t('filters.starter')}</option>
              <option value="professional">
                {t('filters.professional')}
              </option>
              <option value="enterprise">
                {t('filters.enterprise')}
              </option>
            </Select>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Contracts Table */}
      <Card>
        <CardBody>
          {loading ? (
            <Center py={10}>
              <Spinner size="xl" />
            </Center>
          ) : filteredContracts.length === 0 ? (
            <Center py={10}>
              <Text color="gray.500">{t('noContracts')}</Text>
            </Center>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t('table.customer')}</Th>
                    <Th>{t('table.tier')}</Th>
                    <Th isNumeric>{t('table.doors')}</Th>
                    <Th isNumeric>{t('table.value')}</Th>
                    <Th>{t('table.frequency')}</Th>
                    <Th>{t('table.endDate')}</Th>
                    <Th>{t('table.daysRemaining')}</Th>
                    <Th>{t('table.status')}</Th>
                    <Th>{t('table.actions')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredContracts.map((contract) => (
                    <Tr key={contract.id}>
                      <Td>
                        <Box>
                          <Text fontWeight="medium">
                            {contract.customerName}
                          </Text>
                          {contract.companyName && (
                            <Text fontSize="sm" color="gray.600">
                              {contract.companyName}
                            </Text>
                          )}
                          <Text fontSize="sm" color="gray.500">
                            {contract.customerEmail}
                          </Text>
                        </Box>
                      </Td>
                      <Td>
                        <Badge colorScheme={getTierColor(contract.planTier || getPlanTier(contract.doorCount))}>
                          {contract.planTier || getPlanTier(contract.doorCount)}
                        </Badge>
                      </Td>
                      <Td isNumeric>{contract.doorCount}</Td>
                      <Td isNumeric>
                        ${(contract.annualValue || 0).toLocaleString()}
                        <Text fontSize="xs" color="gray.500">
                          ${(contract.monthlyValue || 0).toLocaleString()}/mo
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={contract.paymentFrequency === 'annual' ? 'green' : 'blue'}>
                          {contract.paymentFrequency === 'annual'
                            ? 'Annual'
                            : 'Monthly'}
                        </Badge>
                      </Td>
                      <Td>
                        {contract.endDate
                          ? new Date(contract.endDate).toLocaleDateString()
                          : '-'}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={getDaysRemainingColor(
                            contract.daysRemaining
                          )}
                        >
                          {contract.daysRemaining
                            ? `${contract.daysRemaining} days`
                            : '-'}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(contract.status)}>
                          {contract.status}
                        </Badge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<ViewIcon />}
                            size="sm"
                            variant="ghost"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<ViewIcon />}
                              onClick={() =>
                                navigate(`/admin/contracts/${contract.id}`)
                              }
                            >
                              {t('actions.view')}
                            </MenuItem>
                            <MenuItem
                              icon={<EditIcon />}
                              onClick={() =>
                                navigate(`/admin/contracts/${contract.id}/edit`)
                              }
                            >
                              {t('actions.edit')}
                            </MenuItem>
                            {contract.status === 'active' ||
                            contract.status === 'expiring_soon' ? (
                              <MenuItem
                                icon={<RepeatIcon />}
                                onClick={() =>
                                  handleRenewContract(contract.id)
                                }
                              >
                                {t('actions.renew')}
                              </MenuItem>
                            ) : null}
                            {contract.status === 'active' ? (
                              <MenuItem
                                icon={<CloseIcon />}
                                color="red.500"
                                onClick={() =>
                                  handleCancelContract(contract.id)
                                }
                              >
                                {t('actions.cancel')}
                              </MenuItem>
                            ) : null}
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default ContractsPage;
